import React from 'react';

const HERO_SRC = '/Images/News/hanyuan02.png';
const HERO_ALT =
  'Hanyuan 02 dual-core neutral-atom quantum computer (CAS Cold Atom) with China Mobile Cloud WuYueOS Inside branding at a technology exhibition.';

const Hanyuan02Quantum = () => (
  <>
    <figure className="news-article-figure news-article-figure--hero">
      <img src={HERO_SRC} alt={HERO_ALT} loading="lazy" decoding="async" />
      <figcaption>
        Hanyuan 02 on display: CAS Cold Atom (&quot;中科酷原&quot;) hardware with{' '}
        <strong>WuYueOS Inside</strong> from China Mobile Cloud. The idea is rack-style gear
        you can run in a normal lab, not a building-sized fridge for ultra-cold superconducting
        machines.
      </figcaption>
    </figure>

    <article className="news-article-body">
      <p className="news-lead">
        In early May 2026, Chinese research groups and industry partners showed off{' '}
        <strong>Hanyuan 02</strong> (&quot;汉原 2 号&quot;). Press and official summaries describe
        it as the <strong>first dual-core neutral-atom quantum computer</strong>, with about{' '}
        <strong>200 qubits</strong> in total: two groups of rubidium atoms, 100 of the{' '}
        <sup>87</sup>Rb type and 100 of the <sup>85</sup>Rb type, working as two coordinated
        &quot;cores&quot; instead of one flat grid. Outlets like{' '}
        <a href="https://www.ithome.com/0/947/905.htm" target="_blank" rel="noopener noreferrer">
          IT之家
        </a>{' '}
        and{' '}
        <a
          href="https://www.news.cn/tech/20260514/c6ca7605aced452785c3e45974a0c68f/c.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          新华网
        </a>{' '}
        place it in a bigger story: quantum hardware inching toward cloud services and real
        buyers. What we do <strong>not</strong> have yet is open, third-party proof that this
        box beats classical chips on the AI training or inference jobs your team cares about.
        If the power and reliability numbers hold up under outside testing, though, it still
        matters for how national labs and enterprises think about sitting quantum next to GPUs
        and other accelerators.
      </p>

      <h2>What the announcements say</h2>
      <p>
        Think of <strong>dual-core</strong> here as two separate neutral-atom arrays that work
        together, not one giant lump of qubits. The pitch is easier scaling and less crosstalk
        than pushing everything through a single lattice. The <strong>200-qubit</strong> figure
        comes from pairing those two isotope arrays; supporters say you can run them in parallel
        or use one as a helper to steady the other.
      </p>
      <ul>
        <li>
          <strong>Power and space:</strong> Coverage often cites roughly <strong>under 7 kW</strong>{' '}
          for the whole enclosure and less reliance on huge dilution refrigerators than many other
          approaches. That could mean simpler site prep: less like building a second data hall
          just for one machine.
        </li>
        <li>
          <strong>Compared with Hanyuan 01:</strong> Domestic reports claim sharper control (often
          summarized as accuracy moving from around <strong>90% toward 99%</strong>) and atoms
          staying usable longer (on the order of <strong>tens of seconds to 100+ seconds</strong>).
          Treat those as goals from the lab until other groups reproduce them on the same tasks.
        </li>
        <li>
          <strong>Domestic supply chain:</strong> The story also stresses home-grown lasers,
          packaging, and integration. That is as much about resilience and policy as it is about
          physics.
        </li>
      </ul>

      <h2>What this has to do with AI (the real version)</h2>
      <p>
        Today&apos;s chatbots and copilots run on <strong>classical</strong> silicon (GPUs and
        similar). A quantum computer does <strong>not</strong> drop in as a faster ChatGPT. The
        nearer-term overlap with AI is quieter: hard optimization problems (scheduling, routing,
        portfolios), hybrid quantum-classical sampling, and heavy <strong>physics or chemistry
        simulations</strong> that feed drug discovery and materials work. Those threads connect to
        healthcare and &quot;big data&quot; planning, but through R&amp;D and operations research,
        not by running your LLM on a rubidium chip tomorrow.
      </p>
      <p>
        Xinhua&apos;s broader industry piece talks up <strong>quantum plus AI</strong> ecosystems
        and cloud tooling across several vendors. That reflects where money and roadmaps are
        heading. It is <strong>not</strong> evidence that Hanyuan 02 already runs frontier
        language models for production users.
      </p>

      <h2>What is still open</h2>
      <p>
        There is no well-known, independent benchmark yet that says &quot;on problem X, Hanyuan 02
        beats the best classical approach by Y percent&quot; for concrete ML workloads. For leaders,
        the practical move is to watch the hardware, ask for published fidelity and economics, and
        keep <strong>physics demos</strong> separate from <strong>business advantage</strong>.
      </p>

      <div className="news-insight-box">
        <h3>Takeaway for EWANDS readers</h3>
        <p>
          Hanyuan 02 is interesting as a <strong>hardware bet</strong>: dual neutral-atom cores, a
          large qubit count on paper, and numbers that suggest you might not need a dedicated
          cryo palace to operate it. For <strong>healthcare and analytics</strong>, the payoff is
          further out: better solvers and simulations could change how we design drugs and plan
          large systems, but only after the claims clear outside review and real workflows plug in.
        </p>
      </div>
    </article>
  </>
);

export default Hanyuan02Quantum;
